/**
*****************************************
*                                       *
* NAGARRO  - CSX - CONFIDENTIAL         *
*                                       *
*****************************************
*/

import { Component, OnDestroy, OnInit } from '@angular/core';

import { Alert } from '../shared/model/alert.model';
import { AlertData } from '../shared/model/alert-data.model';
import { AlertServiceService } from '../shared/service/alert-service.service';
import { AppUtilityService } from '../../core/services/app-utility.service';
import { ConfigService } from '../../core/services/config.service';
import { DataStoreService } from '../../core/services/datastore.service';
import { EditModeService } from '../../core/services/edit-mode.service';
import { LmsBaseAppComponent } from '../../core/component/base-app/lms-base-app.component';
import { LmsLoaderService } from '../../core/services/lms-loader.service';
import { LmsLoggerService } from '../../core/services/lms-logger.service';
import { LmsMenuService } from '../../core/services/lms-menu.service';
import { LmsModuleService } from '../../core/services/lms-module.service';
import { PopupService } from '../../core/services/popup.service';
import { RouteChangePopupService } from '../../core/services/route-change-popup.service';
import { StaticDataStoreService } from '../../core/services/static-datastore.service';
import { TabService } from '../../core/services/tab.service';
import { UserProfile } from '../../core/model/user-profile.model';
import { UtilService } from '../../core/services/util.service';
import { PermitAll } from '@csx/security';

@Component({
  selector: 'lms-alert-data',
  templateUrl: './alert-data.component.html',
  styleUrls: ['./alert-data.component.scss']
})
@PermitAll()
export class AlertDataComponent extends LmsBaseAppComponent implements OnInit, OnDestroy {

  alertLevel: string;
  signOffStatus = false;
  alerts: Array<Alert>;
  alertsInfo: AlertData;
  userProfile: UserProfile;
  dirSignOffAlerts: Array<Alert>;
  deskSignOffAlerts: Array<Alert>;
  isTimerPaused = false;
  alertSubscription: any;


  /**
   * Creates an instance of AlertDataComponent.
   * @param {ConfigService} configService
   * @param {LmsMenuService} lmsMenuService
   * @param {LmsModuleService} lmsModuleService
   * @param {LmsLoaderService} lmsLoaderService
   * @param {UtilService} utilService
   * @param {EditModeService} editModeService
   * @param {RouteChangePopupService} routeChangePopupService
   * @param {PopupService} popupService
   * @param {StaticDataStoreService} staticDataStoreService
   * @param {AlertServiceService} alertsService
   *
   * @memberof AlertDataComponent
   */
  constructor(configService: ConfigService,
    lmsMenuService: LmsMenuService,
    lmsModuleService: LmsModuleService,
    private lmsLoaderService: LmsLoaderService,
    utilService: UtilService,
    editModeService: EditModeService,
    routeChangePopupService: RouteChangePopupService,
    popupService: PopupService,
    private staticDataStoreService: StaticDataStoreService,
    private alertsService: AlertServiceService,
    private dataStoreService: DataStoreService,
    private tabService: TabService,
    private appUtilityService: AppUtilityService, private lmsLoggerService: LmsLoggerService) {
    super(configService, lmsMenuService, lmsModuleService, utilService, editModeService, routeChangePopupService, popupService);
    this.service = this.alertsService;
    this.type = this.configService.getConfigData().menu.type.alert;
    this.deskSignOffAlerts = [];
    this.dirSignOffAlerts = [];
    this.alertSubscription = this.alertsService.alerts.subscribe((alerts) => {
      this.alerts = [];
      this.alertsInfo = alerts;
      this.initializeAlerts();
    });
  }

  /**
   * this is the lifeycycle method of this component
   * This method is used to initialize userProfile info
   * from static data store and call the super class init method
   * we also perform several check on alerts to validate them as per alertType
   * @memberof AlertDataComponent
   */
  ngOnInit() {
    // this.appUtilityService.hideAlertCount = true;
    this.userProfile = this.staticDataStoreService.getValue('userProfile');
    super.init(this.search);
    this.initializeAlerts();
    this.alertsService.lLastAccessTime = (new Date()).getTime();
    if (this.selectedTab && this.selectedTab.subTypeDetail && this.selectedTab.subTypeDetail.data && this.selectedTab.subTypeDetail.data.status === 'loaded') {
      this.alertsService.updateLastUpdateAlert().subscribe(data => {
      },
        error => {
          this.lmsLoggerService.logError('Error Updating last Visit Time');
        });
    }
    if ((!this.alerts || this.alerts.length < 1) && this.selectedTab && this.selectedTab.subTypeDetail) {
      this.selectedTab.subTypeDetail.data.message = this.alertsInfo.errorMessage;
      this.lmsModuleService.updateSelectedTab(this.selectedTab);
    }
  }

  /**
   * this method perform various operations on alerts.
   * this method calls method for alert to perform various oprs.
   * @private
   * @memberof AlertDataComponent
   */
  private initializeAlerts() {
    if (this.alertsInfo) {
      this.alerts = this.alertsInfo.alerts;
    }
    this.processSignOffStatusForAlerts();
    this.processAlertsByAccessType();
    this.sortAlerts();
    this.processAlertsBackgroundColor();
    if (this.alerts && this.alerts.length > 0 && this.selectedTab && this.selectedTab.subTypeDetail) {
      this.selectedTab.subTypeDetail.data.status = 'loaded';
      this.lmsModuleService.updateSelectedTab(this.selectedTab);
    }
  }

  /**
   * this method is used to sort alerts
   * @private
   * @memberof AlertDataComponent
   */
  private sortAlerts() {
    if (this.alerts && this.alerts.length > 0) {
      let priorityOneAlert = this.getPriorityOneAlerts(this.alerts);
      let nonPriorityOneAlert = this.getNonPriorityOneAlerts(this.alerts);
      priorityOneAlert = this.sortAlertByCreationDate(priorityOneAlert);
      nonPriorityOneAlert = this.sortAlertByCreationDate(nonPriorityOneAlert);
      const newAlerts = priorityOneAlert.concat(nonPriorityOneAlert);
      this.alerts = newAlerts;
    }
  }

  /**
   * @param alerts
   */
  getPriorityOneAlerts(alerts: Array<Alert>) {
    if (alerts && alerts.length > 0) {
      return this.alerts.filter((alert) => {
        if (alert.priority === '1') {
          return true;
        } else {
          return false;
        }
      });
    } else {
      return [];
    }
  }

  /**
   * @param {Array<Alert>} alerts
   * @returns
   * @memberof AlertDataComponent
   */
  getNonPriorityOneAlerts(alerts: Array<Alert>) {
    if (alerts && alerts.length > 0) {
      return this.alerts.filter((alert) => {
        if (alert.priority !== '1') {
          return true;
        } else {
          return false;
        }
      });
    } else {
      return [];
    }
  }

  /**
   * this method is used to check for any possible background
   * color for alerts
   * @private
   * @memberof AlertDataComponent
   */
  private processAlertsBackgroundColor() {
    if (this.userProfile && this.userProfile.alertType === 'DES' && this.alerts && this.alerts.length > 0) {
      this.alerts.forEach((alert) => {
        if (alert.priority === '1') {
          alert.background = 'red';
        }
      });
    }
  }

  /**
   * this mehtod sorts the given alert array by creation date
   * @private
   * @param {Array<Alert>} alerts
   * @returns
   * @memberof AlertDataComponent
   */
  private sortAlertByCreationDate(alerts: Array<Alert>) {
    return alerts.sort((alert1, alert2) => {
      if (alert1.priority === alert2.priority) {
        if (this.getDateFromString(alert1.creationTs) < this.getDateFromString(alert2.creationTs)) {
          return 1;
        } else if (this.getDateFromString(alert1.creationTs) > this.getDateFromString(alert2.creationTs)) {
          return -1;
        } else if (this.getDateFromString(alert1.creationTs) === this.getDateFromString(alert2.creationTs)) {
          return 0;
        }
      } else if (alert1.priority === '1' && alert2.priority === '2') {
        return -1;
      } else if (alert1.priority === '2' && alert2.priority === '1') {
        return 1;
      } else {
        return 0;
      }
    });
  }

  /**
   * this method converts the given date string
   * to date object
   * @param {string} date
   * @returns {Date}
   * @memberof AlertDataComponent
   */
  getDateFromString(date: string): Date {
    const dateObj = new Date(date);
    return dateObj;
  }

  /**
   * this method is used to sort alerts array by priority
   * @private
   * @param {Array<Alert>} alerts
   * @returns
   * @memberof AlertDataComponent
   */
  private sortAlertByPriority(alerts: Array<Alert>) {
    return alerts.sort((alert1, alert2) => {
      if (parseInt(alert1.priority, 10) < parseInt(alert2.priority, 10)) {
        return 1;
      } else if (parseInt(alert1.priority, 10) > parseInt(alert2.priority, 10)) {
        return -1;
      } else if (parseInt(alert1.priority, 10) === parseInt(alert2.priority, 10)) {
        return 0;
      }
    });
  }

  /**
   * this method is used to process alerts on the basis
   * of given users access type
   * @private
   * @memberof AlertDataComponent
   */
  private processAlertsByAccessType() {
    if (this.alerts && this.alerts.length > 0) {
      if (this.userProfile && this.userProfile.alertType === 'SYS') {
        this.processAlertsForDirUser();
      }
    }
  }

  /**
   * this method is used to process alerts
   * for Director type user
   * @private
   * @memberof AlertDataComponent
   */
  private processAlertsForDirUser() {
    if (this.userProfile && this.userProfile.alertType === 'SYS') {
      this.alerts = this.alerts.filter((alert) => {
        return alert.priority === '1';
      });
    }
  }

  /**
   * this mehtod is used to process signOff Alerts for
   * given alert
   * @private
   * @memberof AlertDataComponent
   */
  private processSignOffStatusForAlerts() {
    if (this.alerts && this.alerts.length > 0) {
      this.alerts.forEach((alert) => {
        if (alert.deskSignOffTs && alert.deskSignOffTs !== '') {
          alert.deskCheckStatus = true;
        } else {
          alert.deskCheckStatus = false;
        }
        if (alert.dirSignOffTs && alert.dirSignOffTs !== '') {
          alert.dirCheckStatus = true;
        } else {
          alert.dirCheckStatus = false;
        }
        alert.createDate = new Date(alert.creationTs);
        alert.expiredDate = new Date(alert.expirationTs);
      });
    }
  }

  /**
   * this method handles the event emitted by child component of this
   * component and process check/uncheck of a perticular alert for desk signOff
   * @param {any} event
   * @memberof AlertDataComponent
   */
  deskSignedEventHandle(event) {
    if (event) {
      this.deskSignOffAlerts = event;
    }
    this.checkForEditModeStatus();
    this.processTimer();
  }

  /**
   * this method is used to process the timer for alert window
   * when user is on the alert screen
   * @private
   * @memberof AlertDataComponent
   */
  private processTimer() {
    if (!this.isTimerPaused) {
      if (this.deskSignOffAlerts.length > 0 || this.dirSignOffAlerts.length > 0) {
        this.alertsService.pauseTimer();
        this.isTimerPaused = true;
      }
    } else if (this.deskSignOffAlerts.length === 0 && this.dirSignOffAlerts.length === 0) {
      this.alertsService.resumeTimer(this.selectedTab);
      this.isTimerPaused = false;
    }
    this.lmsLoaderService.hideLoader();
  }

  /**
   * this method handles the event emitted by child component of this
   * component and process check/uncheck of a perticular alert for Director signOff
   * @param {any} event
   * @memberof AlertDataComponent
   */
  dirSignedEventHandle(event) {
    if (event) {
      this.dirSignOffAlerts = event;
    }
    this.checkForEditModeStatus();
    this.processTimer();
  }

  /**
   * this method is used to handle the view of alerts
   * table when user check the show signedOff checkbox
   * @param {any} event
   * @memberof AlertDataComponent
   */
  handleSignedOffView(event) {
    if (event !== undefined) {
      this.signOffStatus = event;
      this.lmsLoaderService.hideLoader();
    }
  }

  /**
   * this method is used to handle the event emitted by child component
   * when user click on cancel button after checking some alert for signOff
   * @param {any} event
   *
   * @memberof AlertDataComponent
   */
  cancelCheckedSignOff(event) {
    if (event === 'cancel') {
      this.resetSignedOffOpr(false);
      this.checkForEditModeStatus();
    }
  }

  /**
   * this mehtod is used to reset the signOff operations
   * when user click on cancel button or wants to reset the alert window
   * @private
   * @param {boolean} isRefresh
   * @memberof AlertDataComponent
   */
  private resetSignedOffOpr(isRefresh: boolean) {
    if (this.deskSignOffAlerts.length > 0) {
      this.alerts.forEach((alert) => {
        this.deskSignOffAlerts.forEach((checkedAlert) => {
          if (checkedAlert.alertSeq === alert.alertSeq) {
            alert.deskCheckStatus = false;
          }
        });
      });
    }
    if (this.dirSignOffAlerts.length > 0) {
      this.alerts.forEach((alert) => {
        this.dirSignOffAlerts.forEach((checkedAlert) => {
          if (checkedAlert.alertSeq === alert.alertSeq) {
            alert.dirCheckStatus = false;
          }
        });
      });
    }
    this.deskSignOffAlerts = [];
    this.dirSignOffAlerts = [];
    if (!isRefresh) {
      this.alertsService.resumeTimer(this.selectedTab);
      this.isTimerPaused = false;
    }
    this.lmsLoaderService.hideLoader();
  }

  /**
   * this mehtod is used to handle the operation when user clicks
   * on submit button after checking signOff for some alerts and
   * wants to submit the signOff
   * @param {any} event
   * @memberof AlertDataComponent
   */
  submitCheckedSignOff(event) {
    if (event === 'submit') {
      const signedAlert = [];
      if (this.deskSignOffAlerts.length > 0) {
        this.deskSignOffAlerts.forEach((alert) => {
          signedAlert.push(alert);
        });
      }
      if (this.dirSignOffAlerts.length > 0) {
        this.dirSignOffAlerts.forEach((alert) => {
          signedAlert.push(alert);
        });
      }
      const requestData = {
        dirUser: (this.userProfile.alertType && this.userProfile.alertType === 'SYS') ? 'Y' : 'N',
        deskUser: (this.userProfile.alertType && this.userProfile.alertType === 'DES') ? 'Y' : 'N',
        desk: this.userProfile.alertDesk ? this.userProfile.alertDesk : ' '
      };
      if (this.userProfile && this.userProfile.alertType === 'SYS') {
        requestData.desk = ' ';
      }
      const alertToSignOff = this.prepareListofAlertForSignOff(signedAlert);
      this.alertsService.submitSignedOff(alertToSignOff, requestData).subscribe(
        (data) => {
          this.subscribeData(data, requestData);
        }, error => {
          this.selectedTab.subTypeDetail.data.message = this.appUtilityService.getHttpErroMessage(error, false);
          this.lmsModuleService.updateSelectedTab(this.selectedTab);
          this.lmsLoaderService.hideLoader();
        }
      );
    }
  }


  /**
   * helper method for submitCheckedSignOff()
   * mehtod when user gets response form service with
   * priority code between 1 to 4
   * @memberof AlertDataComponent
   */
  subscribeData(data: any, requestData: any) {
    let responseHeader = null;
    responseHeader = data;
    if (this.isNumeric(responseHeader.header.priorityCode)) {
      const pCode = parseInt(responseHeader.header.priorityCode, 10);
      if (pCode > 0 && pCode < 5) {
        this.selectedTab.subTypeDetail.data.status = 'refresh';
        this.alertsService.load(this.selectedTab, false, requestData, responseHeader.header.messageText);
        this.resetSignedOffOpr(false);
        this.isTimerPaused = false;
        this.checkForEditModeStatus();
      } else {
        this.selectedTab.subTypeDetail.data.message = responseHeader.header.messageText;
        this.lmsModuleService.updateSelectedTab(this.selectedTab);
      }
    }
    this.lmsLoaderService.hideLoader();
  }
  /**
   * this method is used to prepare the list of alerts for
   * signOff from alert list
   * @private
   * @param {Array<Alert>} alerts
   * @returns
   * @memberof AlertDataComponent
   */
  private prepareListofAlertForSignOff(alerts: Array<Alert>) {
    const newAlerts = [];
    alerts.forEach((alert) => {
      const alrtObj = {
        id: alert.id,
        priority: alert.priority,
        locoId: alert.locoId,
        trainId: alert.trainId,
        deskId: alert.deskId
      };
      newAlerts.push(alrtObj);
    });
    return newAlerts;
  }

  /**
   * this method is used to refresh the alerts
   * when user clicks on refresh button
   * @param {any} event
   * @memberof AlertDataComponent
   */
  refreshAlertsInfo(event) {
    if (event && event === 'refresh') {
      if (this.selectedTab && this.selectedTab.subTypeDetail) {
        this.resetSignedOffOpr(true);
        this.lmsLoaderService.showLoader();
        this.selectedTab.subTypeDetail.data.status = 'refresh';
        this.alertsService.lLastAccessTime = (new Date()).getTime();
        this.search();
        this.lmsModuleService.updateSelectedTab(this.selectedTab);
      }
    }
  }

  /**
   * this method is used to search the alerts
   * initiate a service call or load from local data store
   * @memberof AlertDataComponent
   */
  public search() {
    if (this.userProfile && this.userProfile.alertType && this.userProfile.alertType !== 'NON') {
      const requestData = {
        dirUser: this.userProfile.alertType === 'SYS' ? 'Y' : 'N',
        deskUser: this.userProfile.alertType === 'DES' ? 'Y' : 'N',
        desk: this.userProfile.alertDesk ? this.userProfile.alertDesk : ''
      };
      if (this.userProfile && this.userProfile.alertType === 'SYS') {
        requestData.desk = ' ';
      }
      this.alertsService.load(this.selectedTab, false, requestData, null);
    }
  }

  /**
   * this method is used to clear the error message
   * when user clicks on close icon on error in red band
   * @memberof AlertDataComponent
   */
  clearErrorMessage() {
    this.selectedTab.subTypeDetail.data.message = '';
    this.lmsModuleService.updateSelectedTab(this.selectedTab);
  }

  checkForEditModeStatus() {
    if (this.alerts && this.alerts.length > 0 && ((this.deskSignOffAlerts && this.deskSignOffAlerts.length > 0) || (this.dirSignOffAlerts && this.dirSignOffAlerts.length > 0))) {
      const editModeStatus = {
        status: true,
        message: 'Unsaved data will be lost. Do you want to continue?'
      };
      this.editModeService.setEditMode(editModeStatus);
    } else {
      const editModeStatus = {
        status: false,
        message: ''
      };
      this.editModeService.setEditMode(editModeStatus);
    }
  }

  private markAllAlertAsRead() {
    if (this.alerts && this.alerts.length > 0) {
      const alertTabData = this.dataStoreService.getValue(this.selectedTab);
      for (const alertData of alertTabData.data.Data['alerts']) {
        alertData.isAlertRead = true;
      }
      this.dataStoreService.putValue(this.selectedTab, alertTabData);
      setTimeout(() => {
        this.alertsService.updateAlertsAfterRead(<AlertData>alertTabData.data.Data);
      }, 100);

    }
  }

  /**
  //  * this is the life cycle method which is called
   * when this component destorys.
   * @memberof AlertDataComponent
   */
  ngOnDestroy() {
    this.markAllAlertAsRead();
    this.alertSubscription.unsubscribe();
    super.destroy();
  }
}
